/**
 * EditorSaveService: persist a serialized scene manifest.
 *
 * 保存由注入的 `remote` 回调完成（main.ts 用 business.api 调鉴权接口
 * PUT /api/v1/hub/scenes/:id/manifest，落库 + 写回静态 scene.json），成功
 * 提示「已保存到服务器」；失败返回明确的失败消息、不再静默降级下载——
 * 「以为保存了其实只下了个文件」正是本次修复的痛点。手动导出走编辑器
 * 工具栏的「导出数据」按钮（download 始终可用）。
 */

import type { SceneManifest } from '../config/manifest';

export interface SaveResult {
    ok: boolean;
    /** Where the manifest ended up, for the editor to surface to the user. */
    message: string;
}

/** 注入式远端保存（main.ts 实现）：成功返回新 revision，失败返回用户可读原因。 */
export type RemoteSceneSave = (
    sceneId: string,
    manifest: SceneManifest
) => Promise<{ ok: true; revision: number } | { ok: false; message: string }>;

export class EditorSaveService {
    /** Save via the injected remote handler. Returns the user-facing result
     *  message; never falls back to a download (use export() deliberately). */
    async save(sceneId: string, manifest: SceneManifest, remote: RemoteSceneSave | null): Promise<SaveResult> {
        if (!remote) {
            return { ok: false, message: '保存失败：未连接业务服务器（可用「导出数据」手动保存 JSON）' };
        }
        const result = await remote(sceneId, manifest);
        if (result.ok) {
            return { ok: true, message: `已保存到服务器（${sceneId}.scene.json，刷新后生效）` };
        }
        return { ok: false, message: `未保存到服务器：${result.message}。可点「导出数据」手动备份 JSON` };
    }

    /** Always-download variant (the editor's "导出数据" action). */
    download(sceneId: string, manifest: SceneManifest): void {
        const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${sceneId}.scene.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
}
